/**
 * Hook V2 — useApprenantCommunications
 *
 * Phase wiring 3 : branche sur `GET /api/v1/v2/apprenants/:id/communications`.
 * Source : envois Qualiopi-V2 trackés en Postgres + Notes Zoho (best-effort).
 * Zoho Activities API pas encore exposée → couverture partielle.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export type CommEventType =
  | 'email_sent'
  | 'email_opened'
  | 'email_replied'
  | 'rdv_past'
  | 'rdv_future'
  | 'note'
  | 'call';

export interface CommEvent {
  id: string;
  type: CommEventType;
  date: string;
  subject: string;
  detail?: string;
  author?: string;
  body?: string;
  emailMeta?: {
    openedAt?: string;
    repliedAt?: string;
    replyExcerpt?: string;
  };
}

export interface ApprenantCommunications {
  apprenantId: string;
  events: CommEvent[];
  lastContactDate: string | null;
  nextScheduled: CommEvent | null;
}

const MARTINE_MOCK: ApprenantCommunications = {
  apprenantId: 'demo-martine',
  lastContactDate: '2026-05-20',
  nextScheduled: null,
  events: [],
};

async function fetchCommunications(
  id: string,
): Promise<ApprenantCommunications & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantCommunications>(
      `/api/v1/v2/apprenants/${id}/communications`,
    );
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantCommunications(id: string): {
  data: (ApprenantCommunications & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-communications', id],
    queryFn: () => fetchCommunications(id),
    staleTime: 30_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
